import React, { useMemo, useEffect } from 'react'
import { useSupplierReconciliation } from '../../../../../api/useSupplierReconciliation'
import { extractAndDisplayError } from '../../../../../utils/errorUtils'
import type { RecMatchRow, components } from '../../../../../types/api/v1'
import { getDaysAgo } from '../../Launchpad/components/utils/getDaysAgo'
import dayjs from 'dayjs'

type SupplierRecSnapshot = components['schemas']['SupplierRecSnapshot']

interface PastDueMetrics {
  total: number
  day_3: number
  day_7: number
  day_20: number
  trend: number
}

interface PastDueOverviewRow {
  supplier_name: string
  supplier_id: string
  bol_count: number
  invoice_count: number
  matched_count: number
  outstanding_amount: number
}

interface PastDueDateRow {
  date: string
  bol_count: number
  invoice_count: number
  matched_count: number
  outstanding_amount: number
}

export interface SupplierRowWithAging {
  supplier_name: string
  supplier_id: string
  bol_count: number
  invoice_count: number
  past_due_bol_count: number
  past_due_invoice_count: number
  matched_count: number
  outstanding_amount: number
  past_due_amount: number
  aging_bucket: '3 Day' | '7 Day' | '20 Day'
}

interface SupplierOption {
  supplier_id: string
  supplier_name: string
}

interface PastDueOverviewTotal {
  bols: number
  invoices: number
  matched: number
  outstanding: number
}

interface Top10PastDueRow {
  supplier_name: string
  supplier_id: string
  bol_count: number
  invoice_count: number
  amount_due: number
}

interface Top10Total {
  bols: number
  invoices: number
  amount: number
}

interface LatePaymentRow {
  invoice_number: string
  supplier_name: string
  payment_date: string
  amount_due: number
  match_id: string
}

interface StatusBreakdown {
  missingBol: {
    count: number
    percentage: number
    topSuppliers: Array<{ name: string; count: number }>
  }
  onHold: {
    count: number
    percentage: number
    topSuppliers: Array<{ name: string; count: number }>
  }
  unapproved: {
    count: number
    percentage: number
    topSuppliers: Array<{ name: string; count: number }>
  }
  open: {
    count: number
    topSuppliers: Array<{ name: string; count: number }>
  }
  overDue: {
    count: number
    topSuppliers: Array<{ name: string; count: number }>
  }
}

interface ManagementDashboardData {
  past_due: PastDueMetrics
  past_due_overview: PastDueOverviewRow[] | SupplierRowWithAging[]
  past_due_overview_total: PastDueOverviewTotal
  top_10_past_due: Top10PastDueRow[]
  top_10_total: Top10Total
  late_payment_summary: LatePaymentRow[]
  late_payment_total: number
  open_items_count: number
  open_items_trend: number
  past_due_balance: number
  past_due_balance_trend: number
  auto_approve_percentage: number
  auto_approve_trend: number
  statusBreakdown: StatusBreakdown
}

interface UseManagementDashboardParams {
  supplier_id?: string
  matches?: RecMatchRow[] // Optional matches from shared context
  groupByDate?: boolean
  selectedSupplier?: string
  trends?: SupplierRecSnapshot[] // Optional trends data from shared context
}

function calculateTrend(oldValue: number, newValue: number): number {
  if (oldValue === 0) return newValue > 0 ? 100 : 0

  const trend = ((newValue - oldValue) / oldValue) * 100

  // Cap trends at -100% to 100% for display purposes
  if (trend > 100) return 100
  if (trend < -100) return -100

  return trend
}

function getTrendFromSnapshots(snapshots: SupplierRecSnapshot[], metricName: keyof SupplierRecSnapshot): number {
  if (!snapshots || snapshots.length < 2) return 0

  // Sort by date to get oldest and newest (oldest first)
  const sorted = [...snapshots].sort((a, b) => new Date(a.date || '').getTime() - new Date(b.date || '').getTime())

  const oldest = sorted[0]
  const newest = sorted[sorted.length - 1]

  const oldValue = typeof oldest[metricName] === 'number' ? (oldest[metricName] as number) : 0
  const newValue = typeof newest[metricName] === 'number' ? (newest[metricName] as number) : 0

  return calculateTrend(oldValue, newValue)
}

export function useManagementDashboardData(params: UseManagementDashboardParams = {}) {
  const { useMatchOverviewOnlyOpen } = useSupplierReconciliation()

  const { start, end } = useMemo(() => {
    const now = new Date()
    const startDate = getDaysAgo(30)
    return { start: startDate, end: now }
  }, [])

  const queryParams = useMemo(
    () => ({
      dateRange: { start, end },
      filters: params.supplier_id ? { supplier_id: params.supplier_id } : {},
    }),
    [start, end, params.supplier_id]
  )

  const {
    data: matchData,
    isLoading,
    error,
  } = useMatchOverviewOnlyOpen(queryParams, {
    enabled: !params.matches,
  })

  // Handle query errors using React Query v5 pattern
  useEffect(() => {
    if (error && !params.matches) {
      extractAndDisplayError(error, 'Failed to load management dashboard data')
    }
  }, [error, params.matches])

  const dashboardData = useMemo<ManagementDashboardData>(() => {
    const matches = params.matches || ((matchData?.rows || []) as RecMatchRow[])
    const now = dayjs()

    const pastDueByDays = { 3: 0, 7: 0, 20: 0, total: 0 }
    const supplierDataMap = new Map<
      string,
      {
        name: string
        bolCount: number
        invoiceCount: number
        matchedCount: number
        outstandingAmount: number
        pastDueAmount: number
        statuses: Map<string, number>
        missingBolCount: number
      }
    >()

    matches.forEach((match) => {
      const supplierId = match.supplier_id
      const supplierName = match.supplier_name

      if (!supplierDataMap.has(supplierId)) {
        supplierDataMap.set(supplierId, {
          name: supplierName,
          bolCount: 0,
          invoiceCount: 0,
          pastDueInvoiceCount: 0,
          matchedCount: 0,
          outstandingAmount: 0,
          pastDueAmount: 0,
          pastDueBolCount: 0,
          statuses: new Map(),
          missingBolCount: 0,
        })
      }

      const supplierData = supplierDataMap.get(supplierId)!

      // Skip voided matches entirely to avoid counting them in invoice/BOL totals
      if (match.status === 'void') {
        return
      }

      // Count total BOLs (for non-voided matches)
      supplierData.bolCount += match.bol_numbers?.filter((bol) => bol.id !== null && bol.id !== undefined).length || 0

      const invoiceCount = match.invoice_numbers?.length || 0
      supplierData.invoiceCount += invoiceCount

      // Count as matched if it has both invoice IDs and BOL IDs AND has matched status
      const hasInvoiceIds = match.invoice_numbers?.some((inv) => inv.id !== null && inv.id !== undefined) || false
      const hasBolIds = match.bol_numbers?.some((bol) => bol.id !== null && bol.id !== undefined) || false
      const hasMatchedStatus = match.status === 'unapproved' || match.status === 'approved' || match.status === 'hold'
      if (hasInvoiceIds && hasBolIds && hasMatchedStatus) {
        supplierData.matchedCount += 1
      }

      const currentStatusCount = supplierData.statuses.get(match.status) || 0
      supplierData.statuses.set(match.status, currentStatusCount + 1)

      if (
        !match.bol_numbers ||
        match.bol_numbers.filter((bol) => bol.id !== null && bol.id !== undefined).length === 0
      ) {
        supplierData.missingBolCount += 1
      }

      if (match.status !== 'approved') {
        const invoiceTotal = match.invoice_total || 0
        supplierData.outstandingAmount += invoiceTotal

        if (match.invoice_due_date) {
          const dueDate = dayjs(match.invoice_due_date)
          const daysPastDue = now.diff(dueDate, 'day')

          if (daysPastDue >= 0) {
            supplierData.pastDueAmount += invoiceTotal
            pastDueByDays.total += invoiceTotal

            // Count invoices only for past due matches
            const pastDueInvoiceCount = match.invoice_numbers?.length || 0
            supplierData.pastDueInvoiceCount += pastDueInvoiceCount

            // Count BOLs only for past due invoices
            const pastDueBolCount =
              match.bol_numbers?.filter((bol) => bol.id !== null && bol.id !== undefined).length || 0
            supplierData.pastDueBolCount += pastDueBolCount

            if (daysPastDue <= 3) {
              pastDueByDays[3] += invoiceTotal
            } else if (daysPastDue <= 7) {
              pastDueByDays[7] += invoiceTotal
            } else {
              pastDueByDays[20] += invoiceTotal // 8+ days (includes 20, 30, 50+ day items)
            }
          }
        }
      }
    })

    const supplierArray = Array.from(supplierDataMap.entries()).map(([id, data]) => {
      const supplierData = {
        supplier_id: id,
        supplier_name: data.name,
        bol_count: data.bolCount,
        invoice_count: data.invoiceCount,
        past_due_invoice_count: data.pastDueInvoiceCount,
        matched_count: data.matchedCount,
        outstanding_amount: data.outstandingAmount,
        past_due_amount: data.pastDueAmount,
        past_due_bol_count: data.pastDueBolCount,
        statuses: data.statuses,
        missing_bol_count: data.missingBolCount,
      }

      return supplierData
    })

    let pastDueOverview: PastDueOverviewRow[] | SupplierRowWithAging[]

    if (params.groupByDate) {
      // Create flat supplier rows with aging bucket data
      const supplierRowsWithAging: SupplierRowWithAging[] = []

      matches.forEach((match) => {
        if (match.status !== 'approved' && match.status !== 'void' && match.invoice_due_date) {
          // Filter by selected supplier if specified
          if (
            params.selectedSupplier &&
            params.selectedSupplier !== '' &&
            match.supplier_name !== params.selectedSupplier
          ) {
            return
          }

          const dueDate = dayjs(match.invoice_due_date)
          const daysPastDue = now.diff(dueDate, 'day')

          // Only include past due items
          if (daysPastDue < 0) return

          // Determine aging bucket
          let agingBucket: '3 Day' | '7 Day' | '20 Day'
          if (daysPastDue <= 3) {
            agingBucket = '3 Day'
          } else if (daysPastDue <= 7) {
            agingBucket = '7 Day'
          } else {
            agingBucket = '20 Day'
          }

          const supplierId = match.supplier_id || 'unknown'
          const supplierName = match.supplier_name || 'Unknown Supplier'

          // Find existing row for this supplier in this aging bucket
          let existingRow = supplierRowsWithAging.find(
            (row) => row.supplier_id === supplierId && row.aging_bucket === agingBucket
          )

          if (!existingRow) {
            existingRow = {
              supplier_id: supplierId,
              supplier_name: supplierName,
              bol_count: 0,
              invoice_count: 0,
              past_due_bol_count: 0,
              past_due_invoice_count: 0,
              matched_count: 0,
              outstanding_amount: 0,
              past_due_amount: 0,
              aging_bucket: agingBucket,
            }
            supplierRowsWithAging.push(existingRow)
          }

          // Count all BOLs and invoices for this match
          existingRow.bol_count +=
            match.bol_numbers?.filter((bol) => bol.id !== null && bol.id !== undefined).length || 0
          existingRow.invoice_count += match.invoice_numbers?.length || 0

          // For aging buckets, since we've already filtered to past due items, count as past due
          existingRow.past_due_bol_count +=
            match.bol_numbers?.filter((bol) => bol.id !== null && bol.id !== undefined).length || 0
          existingRow.past_due_invoice_count += match.invoice_numbers?.length || 0

          // Count as matched if it has both invoice IDs and BOL IDs AND has matched status
          const hasInvoiceIds = match.invoice_numbers?.some((inv) => inv.id !== null && inv.id !== undefined) || false
          const hasBolIds = match.bol_numbers?.some((bol) => bol.id !== null && bol.id !== undefined) || false
          const hasMatchedStatus =
            match.status === 'unapproved' || match.status === 'approved' || match.status === 'hold'
          if (hasInvoiceIds && hasBolIds && hasMatchedStatus) {
            existingRow.matched_count += 1
          }

          const invoiceAmount = match.invoice_total || 0
          existingRow.outstanding_amount += invoiceAmount
          existingRow.past_due_amount += invoiceAmount
        }
      })

      // Filter out empty rows and sort by aging bucket, then by past due amount
      pastDueOverview = supplierRowsWithAging
        .filter((row) => row.past_due_amount > 0)
        .sort((a, b) => {
          // First sort by aging bucket order
          const bucketOrder = { '3 Day': 1, '7 Day': 2, '20 Day': 3 }
          const bucketSort = bucketOrder[a.aging_bucket] - bucketOrder[b.aging_bucket]

          // If same bucket, sort by past due amount descending
          if (bucketSort === 0) {
            return b.past_due_amount - a.past_due_amount
          }

          return bucketSort
        })
    } else {
      const filteredSupplierArray =
        params.selectedSupplier && params.selectedSupplier !== ''
          ? supplierArray.filter((s) => s.supplier_name === params.selectedSupplier)
          : supplierArray

      pastDueOverview = filteredSupplierArray
        .filter((s) => s.past_due_amount > 0)
        .sort((a, b) => b.past_due_amount - a.past_due_amount)
    }

    const pastDueOverviewTotal = pastDueOverview.reduce(
      (acc, item) => ({
        bols: acc.bols + (item.past_due_bol_count || item.bol_count), // Use past due BOL count if available
        invoices: acc.invoices + (item.past_due_invoice_count || item.invoice_count), // Use past due invoice count if available
        matched: acc.matched + item.matched_count,
        outstanding: acc.outstanding + (item.past_due_amount || item.outstanding_amount || 0),
      }),
      { bols: 0, invoices: 0, matched: 0, outstanding: 0 }
    )

    const top10PastDue = supplierArray
      .filter((s) => s.past_due_amount > 0)
      .sort((a, b) => b.past_due_amount - a.past_due_amount)
      .slice(0, 10)
      .map((s) => ({
        supplier_id: s.supplier_id,
        supplier_name: s.supplier_name,
        bol_count: s.past_due_bol_count,
        invoice_count: s.past_due_invoice_count,
        amount_due: s.past_due_amount,
      }))

    const top10Total = top10PastDue.reduce(
      (acc, supplier) => ({
        bols: acc.bols + supplier.bol_count,
        invoices: acc.invoices + supplier.invoice_count,
        amount: acc.amount + supplier.amount_due,
      }),
      { bols: 0, invoices: 0, amount: 0 }
    )

    const latePaymentInvoices: LatePaymentRow[] = []

    // Expand each match to show ALL invoices, not just the first one
    matches.forEach((match) => {
      // Skip voided matches
      if (match.status === 'void') {
        return
      }

      if (!match.invoice_due_date) return
      const dueDate = dayjs(match.invoice_due_date)
      const daysPastDue = now.diff(dueDate, 'day')

      if (daysPastDue >= 0) {
        const invoiceNumbers = match.invoice_numbers || []

        if (invoiceNumbers.length > 0) {
          // Create an entry for each invoice in the match
          invoiceNumbers.forEach((invoice, index) => {
            const invoiceAmount = (match.invoice_total || 0) / invoiceNumbers.length // Distribute amount equally

            latePaymentInvoices.push({
              invoice_number: invoice.number || 'N/A',
              supplier_name: match.supplier_name,
              payment_date: match.invoice_due_date ? dayjs(match.invoice_due_date).format('MM/DD/YYYY') : '',
              amount_due: invoiceAmount,
              match_id: match.id,
            })
          })
        }
      }
    })

    // Sort by amount due (descending) and take first 10 (increased from 5)
    const latePaymentSummary = latePaymentInvoices.sort((a, b) => b.amount_due - a.amount_due).slice(0, 10)

    const latePaymentTotal = latePaymentSummary.reduce((sum, item) => sum + item.amount_due, 0)

    const totalMatches = matches.length

    const missingBolMatches = matches.filter(
      (m) =>
        m.status !== 'void' &&
        (!m.bol_numbers || m.bol_numbers.filter((bol) => bol.id !== null && bol.id !== undefined).length === 0)
    )
    const onHoldMatches = matches.filter((m) => m.status === 'hold')
    const unapprovedMatches = matches.filter(
      (m) => (m.status === 'unapproved' || m.status === 'unmatched') && m.status !== 'void'
    )
    const openMatches = matches.filter((m) => m.status !== 'approved' && m.status !== 'void')
    const overDueMatches = matches.filter((m) => {
      // Skip voided matches
      if (m.status === 'void') {
        return false
      }

      if (!m.invoice_due_date) return false
      const dueDate = dayjs(m.invoice_due_date)
      const daysPastDue = now.diff(dueDate, 'day')
      return daysPastDue >= 0
    })

    const getTopSuppliers = (filteredMatches: RecMatchRow[]) => {
      const supplierCounts = new Map<string, number>()
      filteredMatches.forEach((m) => {
        const count = supplierCounts.get(m.supplier_name) || 0
        supplierCounts.set(m.supplier_name, count + 1)
      })

      return Array.from(supplierCounts.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([name, count]) => ({ name, count }))
    }

    const statusBreakdown: StatusBreakdown = {
      missingBol: {
        count: missingBolMatches.length,
        percentage: totalMatches > 0 ? (missingBolMatches.length / totalMatches) * 100 : 0,
        topSuppliers: getTopSuppliers(missingBolMatches),
      },
      onHold: {
        count: onHoldMatches.length,
        percentage: totalMatches > 0 ? (onHoldMatches.length / totalMatches) * 100 : 0,
        topSuppliers: getTopSuppliers(onHoldMatches),
      },
      unapproved: {
        count: unapprovedMatches.length,
        percentage: totalMatches > 0 ? (unapprovedMatches.length / totalMatches) * 100 : 0,
        topSuppliers: getTopSuppliers(unapprovedMatches),
      },
      open: {
        count: openMatches.length,
        topSuppliers: getTopSuppliers(openMatches),
      },
      overDue: {
        count: overDueMatches.length,
        topSuppliers: getTopSuppliers(overDueMatches),
      },
    }

    // Use latest snapshot values if available, otherwise fall back to calculated values
    const latestSnapshot =
      params.trends && params.trends.length > 0
        ? params.trends.sort((a, b) => new Date(b.date || '').getTime() - new Date(a.date || '').getTime())[0]
        : null

    // Open Items Count - use snapshot value or calculated value
    const openItemsCount = latestSnapshot?.total_open_matches || openMatches.length
    const openItemsTrend = params.trends ? getTrendFromSnapshots(params.trends, 'total_open_matches') : 0

    // Past Due Balance - use snapshot value or calculated value
    const pastDueBalance = latestSnapshot?.total_past_due || pastDueByDays.total
    const pastDueBalanceTrend = params.trends ? getTrendFromSnapshots(params.trends, 'total_past_due') : 0

    // Auto Approve Percentage - use snapshot value or calculated value
    const autoApprovePercentage = latestSnapshot?.auto_match_percentage
      ? latestSnapshot.auto_match_percentage * 100 // Convert from decimal to percentage
      : (() => {
          const autoApprovedMatches = matches.filter((m) => m.status === 'approved' && m.is_auto_approved)
          return totalMatches > 0 ? (autoApprovedMatches.length / totalMatches) * 100 : 0
        })()
    const autoApproveTrend = params.trends ? getTrendFromSnapshots(params.trends, 'auto_match_percentage') : 0

    return {
      past_due: {
        total: pastDueByDays.total,
        day_3: pastDueByDays[3],
        day_7: pastDueByDays[7],
        day_20: pastDueByDays[20],
        trend: pastDueBalanceTrend,
      },
      past_due_overview: pastDueOverview,
      past_due_overview_total: pastDueOverviewTotal,
      top_10_past_due: top10PastDue,
      top_10_total: top10Total,
      late_payment_summary: latePaymentSummary,
      late_payment_total: latePaymentTotal,
      open_items_count: openItemsCount,
      open_items_trend: openItemsTrend,
      past_due_balance: pastDueBalance,
      past_due_balance_trend: pastDueBalanceTrend,
      auto_approve_percentage: autoApprovePercentage,
      auto_approve_trend: autoApproveTrend,
      statusBreakdown,
    }
  }, [matchData, params.matches, params.groupByDate, params.selectedSupplier])

  const supplierOptions = React.useMemo(() => {
    const matches = params.matches || ((matchData?.rows || []) as RecMatchRow[])
    const uniqueSuppliers = new Map<string, SupplierOption>()

    matches.forEach((match) => {
      if (!uniqueSuppliers.has(match.supplier_id)) {
        uniqueSuppliers.set(match.supplier_id, {
          supplier_id: match.supplier_id,
          supplier_name: match.supplier_name,
        })
      }
    })

    return Array.from(uniqueSuppliers.values()).sort((a, b) => a.supplier_name.localeCompare(b.supplier_name))
  }, [params.matches, matchData?.rows])

  return {
    data: dashboardData,
    supplierOptions,
    isLoading: params.matches ? false : isLoading,
    error,
  }
}
